import React, { useEffect, useState } from 'react';
import { EventRegistrationBillingMethod } from '../generated/api/dsv-public/model';
import { Autocomplete, Button, Grid, Stack, TextField } from '@mui/material';
import { Controller, FieldError, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { mapValues } from 'lodash';
import { ObjectShape } from 'yup';
import { BillingInfo, CountryOption, RegistrantInfo } from './form.types';
import { getBillingCountries } from '../generated/api/dsv-public/dsv-public';
import { useFormCheckpoint } from '../context/FormCheckpoint';

type BillingInfoFormProps = {
  registrant?: RegistrantInfo;
  handleSubmitBillingInfo: (billingInfo: BillingInfo) => void;
  handleGoBack: () => void;
};

export const BillingInfoForm: React.FC<BillingInfoFormProps> = ({
  registrant,
  handleSubmitBillingInfo,
  handleGoBack,
}) => {
  const { t, i18n } = useTranslation();
  const { savedBillingInfo } = useFormCheckpoint();
  const [countries, setCountries] = useState<CountryOption[]>([]);

  useEffect(() => {
    getBillingCountries().then(countries => {
      // @ts-ignore
      setCountries(countries.map(country => ({ name: country.name[String(i18n.language)], value: country.value })));
    });
  }, [i18n.language, savedBillingInfo]);

  const billingMethodLabels = {
    [EventRegistrationBillingMethod.EMAIL]: t('forms.billing.billing-method-email'),
    [EventRegistrationBillingMethod.LETTER]: t('forms.billing.billing-method-letter'),
  };

  const schema = Yup.lazy((obj: any) =>
    Yup.object(
      mapValues(obj, (value: any, key: string | string[]) => {
        if (key === 'firstName' || key === 'lastName' || key === 'addressLine' || key === 'city') {
          return Yup.string()
            .max(255, t('forms.common.incorrect-field-pattern.max-length', { maxLength: 255 }) ?? '')
            .required(t('forms.common.incorrect-field-pattern.required') ?? '');
        } else if (key === 'email') {
          return Yup.string()
            .email(t('forms.common.incorrect-field-pattern.email') ?? '')
            .required(t('forms.common.incorrect-field-pattern.required') ?? '');
        } else if (key === 'zipCode') {
          return Yup.string()
            .matches(/^[0-9]{4,5}$/, t('forms.common.incorrect-field-pattern.zip-code') ?? '')
            .required(t('forms.common.incorrect-field-pattern.required') ?? '');
        } else if (key === 'country') {
          return Yup.string()
            .matches(/^[A-Z]{2}$/, t('forms.common.incorrect-field-pattern.country') ?? '')
            .required(t('forms.common.incorrect-field-pattern.required') ?? '');
        }
        // add a default return value to handle the case when the key is not recognized
        return undefined;
      }) as ObjectShape
      // filter out any undefined values from the mapValues output
    ).defined()
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  return (
    <form onSubmit={handleSubmit(handleSubmitBillingInfo as SubmitHandler<FieldValues>)}>
      <h2>{t('forms.billing.title')}</h2>
      <p>{t('forms.billing.intro')}</p>
      <Grid container spacing={2} className="form-registrant-info-row">
        <Grid item xs={12}>
          <TextField
            {...register('firstName')}
            required
            label={t('forms.billing.first-name')}
            defaultValue={savedBillingInfo?.firstName || registrant?.firstName}
            error={!!errors.firstName}
            helperText={errors.firstName ? (errors.firstName as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('lastName')}
            required
            label={t('forms.billing.last-name')}
            defaultValue={savedBillingInfo?.lastName || registrant?.lastName}
            error={!!errors.lastName}
            helperText={errors.lastName ? (errors.lastName as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('addressLine')}
            required
            label={t('forms.billing.address-line')}
            defaultValue={savedBillingInfo?.addressLine || registrant?.addressLine || registrant?.address}
            error={!!errors.addressLine}
            helperText={errors.addressLine ? (errors.addressLine as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('zipCode')}
            required
            label={t('forms.billing.zipCode')}
            defaultValue={savedBillingInfo?.zipCode || registrant?.zipCode}
            error={!!errors.zipCode}
            helperText={errors.zipCode ? (errors.zipCode as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('city')}
            required
            label={t('forms.billing.city')}
            defaultValue={savedBillingInfo?.city || registrant?.city}
            error={!!errors.city}
            helperText={errors.city ? (errors.city as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="country"
            control={control}
            defaultValue={savedBillingInfo?.country || registrant?.country}
            render={({ field: { onChange } }) => (
              <Autocomplete
                onChange={(event, newValue) => {
                  onChange(newValue);
                }}
                fullWidth
                disablePortal
                options={countries.map(option => option.value)}
                getOptionLabel={option => countries.find(country => country.value === option)?.name ?? ''}
                defaultValue={savedBillingInfo?.country || registrant?.country}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('forms.billing.country')}
                    error={!!errors.country}
                    helperText={errors.country ? (errors.country as FieldError).message : undefined}
                    required
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...register('email')}
            required
            label={t('forms.billing.email')}
            defaultValue={savedBillingInfo?.email || registrant?.email}
            error={!!errors.email}
            helperText={errors.email ? (errors.email as FieldError).message : undefined}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="billingMethod"
            control={control}
            defaultValue={savedBillingInfo?.billingMethod || EventRegistrationBillingMethod.EMAIL}
            render={({ field: { onChange } }) => (
              <Autocomplete
                onChange={(event, newValue) => {
                  onChange(newValue);
                }}
                fullWidth
                disablePortal
                defaultValue={savedBillingInfo?.billingMethod || EventRegistrationBillingMethod.EMAIL}
                options={Object.values(EventRegistrationBillingMethod)}
                getOptionLabel={option => billingMethodLabels[option as EventRegistrationBillingMethod]}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('forms.billing.billing-method')}
                    error={!!errors.billingMethod}
                    helperText={errors.billingMethod ? (errors.billingMethod as FieldError).message : undefined}
                    required
                  />
                )}
              />
            )}
          />
        </Grid>
      </Grid>
      <Stack flexDirection="row" justifyContent="center" gap="10px">
        <Button onClick={handleGoBack} variant="outlined">
          {t('forms.common.back')}
        </Button>
        <Button type="submit" variant="outlined">
          {t('forms.common.submit')}
        </Button>
      </Stack>
    </form>
  );
};
